import { ActionType } from 'typesafe-actions';

import PublicationsPage, { Publication } from '@interfaces/publications-page';
import PaginationMeta from '@interfaces/pagination-meta';
import { PublicationRequestParams } from '@interfaces/publication-request-params';

import * as types from './types';
import * as actions from './actions';

export type PublicationsAction = ActionType<typeof actions>;

export interface PublicationsState {
    [key: string]: any;
    readonly listByThemes: { [slug: string]: PublicationsPage };
    readonly listByResearchers: { [id: number]: PublicationsPage };
    readonly one: Partial<Publication>;
    readonly years: {
        list: number[];
        themeSlug?: string;
    };
    readonly rows: Publication[];
    readonly meta: Partial<PaginationMeta>;
    readonly isLoading: boolean;
    readonly params: PublicationRequestParams;
    readonly isLoadingYears: boolean;
    readonly error?: string | boolean;
}

const initialState: PublicationsState = {
    listByThemes: {},
    listByResearchers: {},
    one: {},
    rows: [],
    meta: {},
    years: { list: [] },
    isLoading: false,
    params: { page: 1 },
    isLoadingYears: false,
    error: undefined
};

/* tslint:disable-next-line */
function reducer(state: PublicationsState = initialState, action: PublicationsAction): PublicationsState {
    switch (action.type) {
        case types.FETCH_PUBLICATIONS_PAGE:
            return fetchPublicationsPage(state, action);

        case types.FETCH_PUBLICATIONS_PAGE_SUCCESS:
            return fetchPublicationsPageSuccess(state, action);

        case types.FETCH_PUBLICATIONS_BY_YEARS_PAGE_SUCCESS:
            return fetchPublicationsByListPageSuccess('year', 'listByYears', state, action);

        case types.FETCH_PUBLICATIONS_PAGE_ERROR:
            return fetchPublicationsPageError(state, action);

        case types.FETCH_PUBLICATIONS_BY_THEME_PAGE:
            return fetchPublicationsByThemePage(state);

        case types.FETCH_PUBLICATIONS_BY_THEME_PAGE_SUCCESS:
            return fetchPublicationsByListPageSuccess('slug', 'listByThemes', state, action);

        case types.FETCH_PUBLICATIONS_BY_THEME_PAGE_ERROR:
            return fetchPublicationsPageError(state, action);

        case types.FETCH_PUBLICATIONS_BY_RESEARCHER_PAGE:
            return fetchPublicationsByResearcherPage(state);

        case types.FETCH_PUBLICATIONS_BY_RESEARCHER_PAGE_SUCCESS:
            return fetchPublicationsByListPageSuccess('id', 'listByResearchers', state, action);

        case types.FETCH_PUBLICATIONS_BY_RESEARCHER_PAGE_ERROR:
            return fetchPublicationsPageError(state, action);

        case types.FETCH_PUBLICATION:
            return fetchPublication(state);

        case types.FETCH_PUBLICATION_SUCCESS:
            return fetchPublicationSuccess(state, action);

        case types.FETCH_PUBLICATION_ERROR:
            return fetchPublicationError(state, action);

        case types.FETCH_PUBLICATION_YEARS:
            return fetchPublicationYears(state);

        case types.FETCH_PUBLICATION_YEARS_SUCCESS:
            return fetchPublicationYearsSuccess(state, action);

        case types.FETCH_PUBLICATION_YEARS_ERROR:
            return fetchPublicationYearsError(state, action);

        default:
            return state;
    }
}

function fetchPublicationsPage(state: PublicationsState, action: any) {
    const { payload: params } = action;

    return {
        ...state,
        isLoading: true,
        params,
        error: false
    };
}

function fetchPublicationsPageSuccess(state: PublicationsState, action: any) {
    const { meta, rows: actionRows } = action.payload;
    const rows = meta.pageNumber === 1 ? actionRows : [...state.rows, ...actionRows];

    return {
        ...state,
        meta,
        rows,
        isLoading: false,
        error: false
    };
}

function fetchPublicationsByListPageSuccess(key: string, listName: string, state: PublicationsState, action: any) {
    const { meta, rows: actionRows, params } = action.payload;
    const stateList = state[listName];
    const prevRows = stateList[params[key]] ? stateList[params[key]].rows : [];

    const rows = meta.pageNumber === 1 ? actionRows : [...prevRows, ...actionRows];

    return {
        ...state,
        [listName]: {
            ...stateList,
            [params[key]]: { rows, meta }
        },
        isLoading: false,
        error: false
    };
}

function fetchPublicationsByThemePage(state: PublicationsState) {
    return {
        ...state,
        listByThemes: { ...state.listByThemes || {} },
        isLoading: true,
        error: false
    };
}

function fetchPublicationsByResearcherPage(state: PublicationsState) {
    return {
        ...state,
        listByResearchers: { ...state.listByResearchers || {} },
        isLoading: true,
        error: false
    };
}

function fetchPublicationsPageError(state: PublicationsState, action: any) {
    return {
        ...state,
        isLoading: false,
        error: action.payload
    };
}

function fetchPublication(state: PublicationsState) {
    return {
        ...state,
        one: {},
        isLoading: true,
        error: false
    };
}

function fetchPublicationSuccess(state: PublicationsState, action: any) {
    return {
        ...state,
        one: action.payload,
        isLoading: false,
        error: false
    };
}

function fetchPublicationError(state: PublicationsState, action: any) {
    return {
        ...state,
        isLoading: false,
        error: action.payload
    };
}

function fetchPublicationYears(state: PublicationsState) {
    return {
        ...state,
        years: { list: [] },
        isLoadingYears: true,
        error: false
    };
}

function fetchPublicationYearsSuccess(state: PublicationsState, action: any) {
    const { years, themeSlug } = action.payload;

    return {
        ...state,
        years: { list: years, themeSlug },
        isLoadingYears: false,
        error: false
    };
}

function fetchPublicationYearsError(state: PublicationsState, action: any) {
    return {
        ...state,
        isLoadingYears: false,
        error: action.payload
    };
}

export default reducer;
